/**
 * The dashboard definition: a serializable description of one dashboard.
 *
 * The sibling of {@link ReportSpec}, and deliberately the same kind of thing —
 * plain data with a Zod schema and no behaviour. It is the request body, and
 * it is what `dashboards.definition` holds.
 *
 * A dashboard is TILES plus CONTEXT. The tiles say what to show; the period
 * and filters say over what. Neither carries results: two people opening one
 * shared dashboard run the same queries at their own row scope and see
 * different numbers, which is correct and is why a definition must never cache
 * an answer.
 *
 * See docs/reporting/requirements.md §FR-3 and §FR-4.
 */
import type { DateRangePreset } from '@clockwise/shared/date-range';
import { DATE_RANGE_PRESETS } from '@clockwise/shared/date-range';
import { z } from 'zod';
import { filterNodeSchema, filterRuleSchema } from './filter';
import { reportSpecSchema, VIZ_KINDS } from './spec';

/**
 * Grid columns. A tile's `x` and `w` are in these units.
 *
 * 24 rather than 12: seven KPI tiles cannot share a 12-column row at a
 * readable width (2+2+2+2+2+1+1 leaves two of them too narrow for their own
 * title), and 24 fits seven 3-wide tiles with a 3-wide label beside them.
 */
export const DASHBOARD_COLUMNS = 24;

/**
 * Ceiling on tiles per dashboard.
 *
 * Every tile is at least one query, so this is a bound on what opening a
 * dashboard costs — not a readability rule. Generous enough that nobody
 * building an honest dashboard meets it, low enough that a malformed or
 * hostile definition cannot ask for ten thousand queries.
 */
export const MAX_TILES = 40;

export const TILE_TYPES = ['report', 'metric', 'text', 'unknown'] as const;
export type TileType = (typeof TILE_TYPES)[number];

/** Where a tile sits, in grid units. */
export const tileLayoutSchema = z.object({
  x: z
    .number()
    .int()
    .min(0)
    .max(DASHBOARD_COLUMNS - 1),
  y: z.number().int().min(0),
  w: z.number().int().min(1).max(DASHBOARD_COLUMNS),
  h: z.number().int().min(1).max(100),
});
export type TileLayout = z.infer<typeof tileLayoutSchema>;

/**
 * How a report tile draws, overriding the saved report's own choice.
 *
 * On the TILE, not on the report, because the same report legitimately appears
 * as a table on one dashboard and a stacked bar on another. Pushing this back
 * onto the report would make placing it twice a choice between them.
 */
export const tileViewSchema = z.object({
  viz: z.enum(VIZ_KINDS).default('table'),
  stacked: z.boolean().default(false),
});
export type TileView = z.infer<typeof tileViewSchema>;

const tileBase = {
  id: z.string().min(1).max(64),
  layout: tileLayoutSchema,
  /** Overrides the source's name. Absent means "use the report's own". */
  title: z.string().max(200).optional(),
};

/** Ceiling on a linked tile's own filters — the same as a report's own. */
export const MAX_TILE_FILTERS = 20;

/**
 * A report, placed. ONE tile type, with two sources — exactly one of them.
 *
 * LINKED (`reportId`): a saved custom report, BY REFERENCE. Editing the report
 * reaches every dashboard showing it, which is the point of linking one rather
 * than copying it. The cost is that deleting or unsharing a report breaks its
 * tiles — the web draws a tombstone saying what is missing instead of a hole.
 * A linked tile may add `filters` of its own on top of the report's: they are
 * ANDed on, so a tile can only ever narrow the report it shows.
 *
 * DASHBOARD REPORT (`spec`): a report built on the dashboard and stored on the
 * tile. It is never a row in `report_definitions`, so it never appears in
 * Custom Reports, in anybody's report list or in a drill menu, and it goes
 * wherever the dashboard goes — shared, duplicated, transferred and deleted
 * with it. Its filters are its spec's own, so it takes no `filters`, and it must
 * carry a `title`: that is its name, since there is no saved report to read one
 * from.
 *
 * Why one type and not two: to the person arranging a dashboard both are "a
 * report on a tile", and a second type would be a second menu, a second
 * renderer and a second place for the two to drift. The spec is the same
 * `reportSpecSchema` a saved report stores, so turning one kind into the other
 * — "Save as custom report", "Make a dashboard copy" — is a move of one field.
 *
 * `view` decides how BOTH kinds draw: the builder's chart choice is written to
 * it when a dashboard report is applied, so there is one place to read it.
 *
 * An older build's schema requires `reportId`, so it reads a dashboard report
 * as an `unknown` tombstone that keeps the original under `raw` — restored by
 * `parseDefinition` once the newer build is back. Ship the API first: an old
 * API refuses the tile with a 400 rather than dropping it.
 */
export const reportTileSchema = z
  .object({
    ...tileBase,
    type: z.literal('report'),
    reportId: z.string().uuid().optional(),
    spec: reportSpecSchema.optional(),
    filters: z.array(filterNodeSchema).max(MAX_TILE_FILTERS).optional(),
    view: tileViewSchema.default({ viz: 'table', stacked: false }),
  })
  .superRefine((tile, ctx) => {
    const linked = tile.reportId !== undefined;
    const inline = tile.spec !== undefined;
    if (linked === inline) {
      ctx.addIssue({
        code: 'custom',
        message: 'a report tile needs exactly one of a saved report or its own report',
        path: ['reportId'],
      });
    }
    if (inline && (tile.title ?? '').trim() === '') {
      ctx.addIssue({
        code: 'custom',
        message: 'a report built on the dashboard needs a title',
        path: ['title'],
      });
    }
    if (inline && tile.filters !== undefined && tile.filters.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'a report built on the dashboard keeps its filters in its own report',
        path: ['filters'],
      });
    }
  });

/**
 * A METRIC: one measure, one period, its movement, and its history.
 *
 * Named for how it behaves rather than for what it looks like. "KPI" says a
 * number is important and nothing about which number, over what, compared to
 * what — and every one of those was a decision somebody had to make, so they
 * are written here rather than left to each tile.
 *
 * THE CONTRACT, in full:
 *
 *  - **One measure.** Not a shortlist. A tile showing two numbers is a table
 *    with ambitions, and the comparison below would belong to neither.
 *
 *  - **The reader's window, never a summed range.** On a dashboard the figure
 *    is the measure over the whole window the reader chose — "This quarter"
 *    is the quarter — asked of the database as its own question
 *    (`metricComparisonSpecs`). Never added up from the series, because a
 *    ratio — utilization, billable share — cannot be summed across periods.
 *
 *  - **Compared against its MIRROR** — the same span immediately before: the
 *    previous quarter for a quarter, the previous year for a year. While the
 *    window is still running, the mirror is cut to the same day, so
 *    quarter-to-date is compared with the previous quarter to that day and
 *    never with the whole of it — except a single month, which is compared
 *    with the whole of last month (`comparisonWindows`).
 *
 *  - **History covers the period AND its mirror**, in the grain the spec
 *    groups by, at least twelve of them, running to today — so the sparkline
 *    shows every point either figure was made from. Each point names its
 *    period and value on hover, an unfinished one as "to date".
 *
 * Where there is no reader's window to mirror — the builder's preview — the
 * figure is ONE grain of the series instead: the last COMPLETE one, or the
 * current one shown uncompared when the reader selected it, compared with the
 * preceding grain only when the two genuinely adjoin (`selectMetricPeriods`).
 *
 * The series and the two window questions share one effective spec — the same
 * filters and scope — so the figure, its comparison and the history cannot
 * disagree about anything but the dates they were asked over.
 *
 * The spec is INLINE rather than a reference, because a metric is composed on
 * the dashboard — "total billable hours, big, at the top" is not a report
 * anybody wants in their reports list. A saved report can still be shown as a
 * metric: that is a `report` tile with `view.viz = 'metric'`, and it obeys
 * every rule above.
 *
 * See `selectMetricPeriods` for the period rules and `metricWindow` for how
 * far back the history reaches.
 */
export const metricTileSchema = z.object({
  ...tileBase,
  type: z.literal('metric'),
  spec: reportSpecSchema,
});

/** A note. Not data — headings and commentary, so a dashboard can be read. */
export const textTileSchema = z.object({
  ...tileBase,
  type: z.literal('text'),
  /**
   * PLAIN TEXT, rendered as typed. Not markdown: nothing in this repo parses
   * or sanitises it, and adding a renderer means adding an injection surface
   * to a field one user fills in and others read.
   */
  body: z.string().max(4000).default(''),
});

/**
 * A tile this build does not understand.
 *
 * NEVER AUTHORED — {@link dashboardDefinitionSchema} produces it by falling
 * back, and it is how a dashboard survives a tile type that was renamed,
 * removed, or added by a newer deploy. The alternative is a definition that
 * fails to parse, which loses the other eleven tiles as well as the one nobody
 * can draw. The original is kept verbatim so a downgrade is not destructive.
 */
export const unknownTileSchema = z.object({
  ...tileBase,
  type: z.literal('unknown'),
  raw: z.unknown(),
});

export const dashboardTileSchema = z.discriminatedUnion('type', [
  reportTileSchema,
  metricTileSchema,
  textTileSchema,
  unknownTileSchema,
]);
export type DashboardTile = z.infer<typeof dashboardTileSchema>;
export type ReportTile = z.infer<typeof reportTileSchema>;
/** A report tile pointing at a saved custom report. */
export type LinkedReportTile = ReportTile & { reportId: string; spec?: undefined };
/** A report tile carrying a report built on the dashboard. */
export type DashboardReportTile = ReportTile & {
  spec: z.infer<typeof reportSpecSchema>;
  reportId?: undefined;
};

/** Is this a report tile linked to a saved custom report? */
export const isLinkedReport = (tile: DashboardTile): tile is LinkedReportTile =>
  tile.type === 'report' && tile.reportId !== undefined;

/** Is this a report tile carrying its own report, built on the dashboard? */
export const isDashboardReport = (tile: DashboardTile): tile is DashboardReportTile =>
  tile.type === 'report' && tile.spec !== undefined;
export type MetricTile = z.infer<typeof metricTileSchema>;
export type TextTile = z.infer<typeof textTileSchema>;
export type UnknownTile = z.infer<typeof unknownTileSchema>;

/**
 * One filter offered on the dashboard's bar.
 *
 * Named by DIMENSION ID, and matched to a tile's model by that id alone. Not
 * by label: `srController` and `accountSrController` are both "Sr. Controller"
 * and mean different people — the org head of whoever logged the hours, and
 * whoever runs the account. The ids differ precisely so a filter cannot cross
 * between the two, and a bar that merged them by label would undo that. Two
 * chips reading "Sr. Controller" is the correct outcome; `label` below exists
 * to tell them apart.
 */
export const dashboardFilterSchema = z.object({
  dimension: z.string().min(1).max(120),
  /** Overrides the dimension's own label, to disambiguate a collision. */
  label: z.string().max(120).optional(),
});
export type DashboardFilterDecl = z.infer<typeof dashboardFilterSchema>;

/**
 * The dashboard's default period.
 *
 * A macro plus its answer, exactly as a spec records one, and for the same
 * reason: "current month" is a standing question, and storing only the two
 * dates it resolved to on the day it was saved turns it into a fixed one.
 */
export const dashboardPeriodSchema = z.object({
  preset: z.enum(DATE_RANGE_PRESETS).optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type DashboardPeriod = z.infer<typeof dashboardPeriodSchema>;

/** How many filters an author may pin to a dashboard's bar. */
export const MAX_DASHBOARD_FILTERS = 20;

/**
 * The filters a dashboard OPENS with — its saved view, the counterpart of
 * `period`.
 *
 * Rules, not decls: `filters` above says which chips the bar offers, this says
 * what they are set to when nobody has said otherwise. Kept apart because the
 * two are independent — a pinned chip may open empty, and a default may sit on
 * a field nobody pinned.
 *
 * Written only by "Save for everyone". A reader's own filters still live in the
 * URL and override these whole (FR-4.8): a link that carries `f` is about that
 * link's filters, and one that does not opens on these.
 */
export const dashboardDefaultFiltersSchema = z.array(filterRuleSchema).max(MAX_DASHBOARD_FILTERS);

/** The whole definition. */
export const dashboardDefinitionSchema = z.object({
  period: dashboardPeriodSchema,
  filters: z.array(dashboardFilterSchema).max(MAX_DASHBOARD_FILTERS).default([]),
  defaultFilters: dashboardDefaultFiltersSchema.default([]),
  tiles: z.array(dashboardTileSchema).max(MAX_TILES).default([]),
});
export type DashboardDefinition = z.infer<typeof dashboardDefinitionSchema>;

/**
 * Parse a stored definition, degrading a tile rather than losing the dashboard.
 *
 * Two-stage on purpose. The OUTER shape must be right — a definition with no
 * period is not a dashboard and there is nothing sensible to draw — but a
 * single tile that fails to parse is a local problem, and refusing the whole
 * document for it takes down eleven working tiles to punish one. Anything that
 * does not parse becomes an `unknown` tile carrying its original, which the UI
 * draws as a tombstone naming what it cannot show.
 *
 * Use this for anything READ from storage or from another deploy. Use
 * {@link dashboardDefinitionSchema} directly to validate something a user just
 * submitted, where a malformed tile should be a 400 rather than a tombstone.
 */
export function parseDefinition(input: unknown): DashboardDefinition {
  const outer = z
    .object({
      period: dashboardPeriodSchema,
      filters: z.array(dashboardFilterSchema).max(MAX_DASHBOARD_FILTERS).catch([]).default([]),
      // Dropped WHOLE when unreadable rather than rule by rule: a default with
      // one rule missing is a different view, and opening the dashboard on it
      // would show numbers nobody chose. Unfiltered is at least an honest one.
      defaultFilters: dashboardDefaultFiltersSchema.catch([]).default([]),
      // NO `.max()` here, unlike the strict schema. The ceiling belongs on the
      // WRITE path, where exceeding it is a request to refuse; enforcing it on
      // the read path would make this function throw on the whole document —
      // the one thing it exists not to do. A definition longer than the
      // ceiling (written before it was lowered, or by a newer deploy) is
      // truncated below instead, so the dashboard still opens.
      tiles: z.array(z.unknown()).default([]),
    })
    .parse(input);

  const tiles: DashboardTile[] = outer.tiles.slice(0, MAX_TILES).map((raw, index) => {
    const parsed = dashboardTileSchema.safeParse(raw);
    if (parsed.success) {
      // A TOMBSTONE THIS BUILD CAN NOW READ. When an older deploy could not
      // draw a tile it stored the original under `raw` and marked the tile
      // `unknown`; if that original parses here, the tile is restored rather
      // than left as a tombstone forever. Without this the "kept verbatim so a
      // downgrade is not destructive" promise above only holds until somebody
      // on the older deploy presses Save, after which the tile is a headstone
      // with the body still in the coffin and nobody ever opening it.
      if (parsed.data.type === 'unknown') {
        const original = dashboardTileSchema.safeParse(parsed.data.raw);
        if (original.success && original.data.type !== 'unknown') return original.data;
      }
      return parsed.data;
    }
    return {
      // A tombstone still has to occupy the right cells, or every tile below
      // it moves and the dashboard rearranges itself on a bad deploy. The
      // layout is read leniently for that reason — it is the one field worth
      // salvaging from a tile that is otherwise unreadable.
      id: idOf(raw) ?? `unknown-${index}`,
      type: 'unknown',
      layout: layoutOf(raw) ?? { x: 0, y: index, w: 8, h: 6 },
      title: titleOf(raw),
      raw,
    };
  });

  return { ...outer, tiles };
}

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : undefined;

function idOf(raw: unknown): string | undefined {
  const id = asRecord(raw)?.id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

function titleOf(raw: unknown): string | undefined {
  const title = asRecord(raw)?.title;
  return typeof title === 'string' ? title : undefined;
}

function layoutOf(raw: unknown): TileLayout | undefined {
  const parsed = tileLayoutSchema.safeParse(asRecord(raw)?.layout);
  return parsed.success ? parsed.data : undefined;
}

/**
 * An empty dashboard, over a window.
 *
 * The counterpart of `emptySpec`: a definition a builder can start editing,
 * with no tiles and no filters — the filter bar derives itself from whatever
 * gets placed.
 */
export function emptyDefinition(period: DashboardPeriod): DashboardDefinition {
  return {
    period,
    filters: [],
    defaultFilters: [],
    tiles: [],
  };
}

/** The models a definition's tiles read, deduplicated. For catalog fetching. */
export function definitionModels(
  definition: DashboardDefinition,
  reportModel: (reportId: string) => string | undefined,
): string[] {
  const out = new Set<string>();
  for (const tile of definition.tiles) {
    if (tile.type === 'metric') out.add(tile.spec.model);
    if (isDashboardReport(tile)) out.add(tile.spec.model);
    if (isLinkedReport(tile)) {
      const model = reportModel(tile.reportId);
      if (model) out.add(model);
    }
  }
  return [...out];
}

export type { DateRangePreset };
